/* eslint-disable camelcase */
import styled from '@emotion/styled';
import { useState } from 'react';
import { Theme } from 'ui_system/theme';
import { t } from 'services/translation';
import { MobileBackNav } from 'ui_system/modals/MobileBackNav';
import { ReactModal } from 'ui_system/modals/ReactModal';
import { capitalize } from 'lib/string_utils';
import { SlotTicketCapacity } from 'views/ui/form_elements/multi_select_dropdown/GuestOfGuest/MultiSelectGuest';
import {
  ControlSectionWrapper,
  RemoveSection,
  RemoveIcon,
  RemoveButton,
  DesktopOptionWrapper,
  TicketIdText,
  TicketDescriptionText,
  GuestNameWrapper,
  GuestName,
  PrimaryText,
  MobileMainWrapper,
  MobileOptionWrapper,
  GuestOptionsWrapper,
  AddGuestWrapper,
  AddGuestText,
  AddIcon,
  MobileHeader,
  DoneButtonContainer,
  DoneButton,
  EditSection,
  EditButton,
  EditIcon,
} from './MultiSelectGuestOption.styles';
import '../index.scss';
import GuestForm from './GuestForm';

interface MultiSelectGuestProps {
  guestList?: Guest[];
  isMobile?: boolean;
  removeGuest: any;
  onClose?: (data: any) => void;
  onDone?: (data: any) => void;
  units: any[];
  addGuest: (data: any) => void;
  updateGuest: (data: GuestObject) => void;
  onUpdateGuestDetail: () => void;
  country: string;
  slotTicketCapacities: SlotTicketCapacity[];
}

export interface GuestFormProps {
  onAddGuest(data: GuestObject): void;
  onEditGuest(data: GuestObject): void;
  onUpdateGuestDetail(): void;
  units: any[];
  country?: string;
  type: string;
  guest?: any;
  slotTicketCapacities?: SlotTicketCapacity[];
  guestList?: Guest[];
}

interface GuestObject {
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  ticketUnitType: string | null;
  unitTypeId: any;
}

const GuestFormModal = styled(
  ReactModal<GuestFormProps>(GuestForm, { isBackgroundClickable: false })
)`
  padding: 0;
  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    width: 600px;
  }
`;

interface Guest {
  id: number;
  uiId: string;
  name: string;
  isPrimary: boolean;
  unitType: number;
  ticketUnitType: string;
  ticketPrice: string;
  ticketUniqId: string;
}

export const MultiSelectGuestOption = ({
  guestList,
  isMobile,
  removeGuest,
  onClose,
  onDone,
  addGuest,
  updateGuest,
  onUpdateGuestDetail,
  units,
  country,
  slotTicketCapacities = [],
}: MultiSelectGuestProps): JSX.Element => {
  const [addGuestModal, setAddGuestModal] = useState(false);
  const [guestFormModalType, setGuestFormModalType] = useState('add');
  const [editGuest, setEditGuest] = useState({});

  const onClickAddGuest = (modalType: any) => {
    setAddGuestModal(true);
    setGuestFormModalType(modalType);
    setEditGuest({});
  };

  const clickEditGuest = (modalType: any, guest: any) => {
    setAddGuestModal(true);
    setGuestFormModalType(modalType);
    setEditGuest(guest);
  };

  const closeAddModal = (guestObj: GuestObject) => {
    addGuest(guestObj);
    setAddGuestModal(false);
  };

  const closeEditModal = (guestObj: GuestObject) => {
    updateGuest(guestObj);
    setAddGuestModal(false);
  };

  const guestControls = (guest: Guest) => {
    return (
      <ControlSectionWrapper>
        <EditSection
          data-testid="edit-sec"
          onClick={() => clickEditGuest('edit', guest)}
        >
          <EditIcon />
          <EditButton data-testid="quantity-update">Edit</EditButton>
        </EditSection>
        <RemoveSection
          data-testid="remove-sec"
          onClick={!guest.isPrimary ? () => removeGuest(guest) : undefined}
        >
          <RemoveIcon isprimary={guest.isPrimary ? 'true' : ''} />
          <RemoveButton
            isprimary={!!guest.isPrimary}
            data-testid="quantity-decrease"
          >
            Remove
          </RemoveButton>
        </RemoveSection>
      </ControlSectionWrapper>
    );
  };

  const renderGuestListDesktop = (guest: Guest, isLast: boolean) => {
    const { ticketUniqId, ticketUnitType, ticketPrice, isPrimary } = guest;
    return (
      <DesktopOptionWrapper
        key={guest.uiId}
        data-testid="multi-select-option"
        islast={isLast}
      >
        <GuestNameWrapper>
          <TicketIdText data-testid="ticket-id">
            {ticketUniqId ? `ID: ${ticketUniqId}` : 'n/a'}
          </TicketIdText>
          <br />
          <GuestName capitalize={!!guest.name.trim()} data-testid="guest-name">
            {guest.name.trim() || 'Name unavailable'}
          </GuestName>
          {isPrimary && (
            <PrimaryText data-testid="primary-tag">Primary</PrimaryText>
          )}
          {guestControls(guest)}
        </GuestNameWrapper>
        <div className="pricing-breakdown">
          <TicketDescriptionText
            className="description"
            data-testid="ticket-unit-type"
          >
            {capitalize(ticketUnitType)} {ticketPrice && `@ ${ticketPrice}`}
          </TicketDescriptionText>
        </div>
      </DesktopOptionWrapper>
    );
  };

  const renderGuestListMobile = (guest: Guest) => {
    const { ticketUniqId, name, isPrimary, ticketUnitType } = guest;
    return (
      <MobileMainWrapper key={guest.uiId}>
        <MobileOptionWrapper>
          {!!ticketUniqId && <TicketIdText>ID: {ticketUniqId}</TicketIdText>}
          <GuestNameWrapper>
            <GuestName capitalize={!!name.trim()}>
              {name.trim() || 'Name unavailable'}
            </GuestName>
            {isPrimary && <PrimaryText>Primary</PrimaryText>}
          </GuestNameWrapper>
          <div className="pricing-breakdown">
            <TicketDescriptionText className="description">
              {ticketUnitType}
            </TicketDescriptionText>
          </div>
          {guestControls(guest)}
        </MobileOptionWrapper>
      </MobileMainWrapper>
    );
  };
  const showGuestListScreen = isMobile && !addGuestModal;

  return (
    <>
      {!isMobile && (
        <>
          <GuestOptionsWrapper data-testid="multi-selector-guest">
            {guestList?.map((guest: Guest, index: number) => {
              const isLast = index === guestList.length - 1;
              return renderGuestListDesktop(guest, isLast);
            })}
          </GuestOptionsWrapper>
          {addGuestModal && (
            <GuestFormModal
              type={guestFormModalType}
              isOpen={addGuestModal}
              onAddGuest={closeAddModal}
              onEditGuest={closeEditModal}
              units={units}
              onClose={() => {
                setAddGuestModal(false);
              }}
              onUpdateGuestDetail={onUpdateGuestDetail}
              country={country}
              guest={editGuest}
              slotTicketCapacities={slotTicketCapacities}
              guestList={guestList}
            />
          )}
        </>
      )}
      {isMobile && (
        <>
          <MobileHeader data-testid="multi-selector-guest-mb">
            {onClose && <MobileBackNav title="Guests" onClose={onClose} />}
          </MobileHeader>
          {guestList?.map((guest: Guest) => {
            return renderGuestListMobile(guest);
          })}
        </>
      )}
      {isMobile && !showGuestListScreen && (
        <GuestFormModal
          type={guestFormModalType}
          isOpen={addGuestModal}
          onAddGuest={closeAddModal}
          onEditGuest={closeEditModal}
          units={units}
          onClose={() => {
            setAddGuestModal(false);
          }}
          onUpdateGuestDetail={onUpdateGuestDetail}
          country={country}
          guest={editGuest}
          slotTicketCapacities={slotTicketCapacities}
          guestList={guestList}
        />
      )}
      <AddGuestWrapper
        onClick={() => onClickAddGuest('add')}
        data-testid="add-guest"
      >
        <AddIcon />
        <AddGuestText> Add another guest </AddGuestText>
      </AddGuestWrapper>
      {isMobile && (
        <DoneButtonContainer>
          <DoneButton
            text={t('DONE')}
            onClick={onDone}
            disabled={false}
            faded={false}
            className={undefined}
            inverted={undefined}
          />
        </DoneButtonContainer>
      )}
    </>
  );
};
